// medplum-access.js — ONLINE-PRIMARY chart access grant / dept-upgrade / revoke.
// The browser invokes the deployed `access-grant` Bot (cfg.bots.accessGrant)
// DIRECTLY as the logged-in doctor via the shared getMedplum() session — the
// same executeBot idiom as signViaBot (medplum-writes.js). The Bot is the ONLY
// writer of access stamps: it merges the grantee into the Patient's
// meta.accounts and restamps the WHOLE patient compartment via Medplum's native
// Patient/$set-accounts propagate:true, writes the AuditEvent, and notifies the
// patient's original care team with a one-tap-revoke Task (contract proven by
// scripts/medplum/_spike-grant-revoke-bot.mjs, 42/42).
//
// TAMPER-PROOF BY CONSTRUCTION: the Bot derives WHO the grant lands on from
// event.requester — set server-side from the authenticated ProjectMembership,
// never from input — so this client can only ever grant to the CALLER's own
// %profile (grant) or the caller's own department (upgrade). There is nothing
// here to spoof.
//
// FAIL LOUD (AGENTS §1.3): every function throws on { ok:false } and on
// { authorized:false } (err.authorized === false, err.reason carries the Bot's
// reason) — the caller decides the degraded UI (e.g. the timeline drawer falls
// back to its designed AccessDenied request/break-glass state).
import { getMedplum } from './medplum-client.js';

// Spike-proven ($set-accounts propagate:true): the _compartment search index
// takes ~600-800ms to settle after a restamp. A caller that re-fetches the
// chart right after a successful grant should wait this long first (and bust
// MedplumClient's search cache), or the very next search can still 403/empty.
export const ACCESS_INDEX_SETTLE_MS = 800;

// Executes the access-grant Bot with the doctor's own token and unwraps +
// validates the result. Input per the decreed Bot contract:
//   { mode: 'grant'|'upgrade'|'revoke'|'check', patientId, granteeRef?,
//     reason?, flavor? } — reason is threaded to the Bot for the audit trail /
//     notify-Task; flavor:'break-glass' marks an emergency grant (reason
//     REQUIRED — enforced client-side in grantChartAccess and again by the Bot).
async function execAccessBot(input) {
  const { medplum, cfg } = await getMedplum();
  if (!cfg || !cfg.bots || !cfg.bots.accessGrant) {
    throw new Error('access-grant Bot not configured (cfg.bots.accessGrant missing — rerun seed-demo-queue)');
  }
  // executeBot wraps the handler return inconsistently ({returnValue} vs the
  // value directly) — defensively unwrap both, the pattern the spikes proved.
  const out = await medplum.executeBot(cfg.bots.accessGrant, input);
  const res = (out && out.returnValue) || out;
  if (res && res.authorized === false) {
    const err = new Error(`access ${input.mode} not authorized${res.reason ? ': ' + res.reason : ''}`);
    err.authorized = false;
    err.reason = res.reason;
    throw err;
  }
  if (!res || res.ok !== true) {
    throw new Error(`access ${input.mode} failed${res && res.error ? ': ' + res.error : ''}`);
  }
  return res; // { ok:true, mode, grantee, alreadyGranted?, resourcesUpdated? }
}

// Personal on-open grant: stamps the CALLER's own Practitioner ref onto the
// patient's whole compartment. Idempotent — an existing stamp returns
// { ok:true, alreadyGranted:true } (no duplicate notify Task).
//
// THE ONE GRANT PATH (access-model decree): the old request-access→approve
// queue and the separate break-glass endpoint are both superseded by this
// single Bot call. `reason` (free text) rides to the Bot so it lands on the
// AuditEvent + the care-team notify-Task; `flavor:'break-glass'` marks an
// EMERGENCY grant — reason becomes mandatory (checked here, loudly, before any
// network call) and the Bot flags the grant for the audit trail. Governance is
// after-the-fact: the original care team gets the notify-Task with one-tap
// revoke (see AccessGrantedPanel in Tasks.jsx), not a pre-approval inbox.
export async function grantChartAccess({ patientId, reason, flavor }) {
  if (!patientId) throw new Error('grantChartAccess: missing patientId');
  const text = String(reason || '').trim();
  if (flavor === 'break-glass' && !text) {
    throw new Error('grantChartAccess: break-glass requires a reason');
  }
  const input = { mode: 'grant', patientId };
  if (text) input.reason = text;
  if (flavor) input.flavor = flavor;
  return execAccessBot(input);
}

// Department upgrade: the Bot resolves the CALLER's department from THEIR
// PractitionerRole.organization and stamps that Organization ref — the whole
// department can then open the chart.
export async function upgradeChartAccessToDepartment({ patientId }) {
  if (!patientId) throw new Error('upgradeChartAccessToDepartment: missing patientId');
  return execAccessBot({ mode: 'upgrade', patientId });
}

// Check (read-only introspection): does the logged-in doctor currently have
// chart access to this patient? Zero writes. Returns the Bot's
// { ok:true, mode:'check', hasChartAccess, isHomeTeam }. The drawer uses this to
// disambiguate an EMPTY history load — once Patient demographics are open to
// every doctor, an unauthorized cross-department read RLS-returns [] (not a
// 403), so "no visits" alone can't tell "no access" from "genuinely empty".
export async function checkChartAccess({ patientId }) {
  if (!patientId) throw new Error('checkChartAccess: missing patientId');
  return execAccessBot({ mode: 'check', patientId });
}

// Revoke (un-stamp). Without granteeRef the Bot removes the CALLER's own
// stamps (self-revoke); with granteeRef it removes that grantee — allowed only
// for the patient's generalPractitioner / home-department members (the Bot
// enforces this; an unauthorized caller gets the loud authorized:false throw).
export async function revokeChartAccess({ patientId, granteeRef }) {
  if (!patientId) throw new Error('revokeChartAccess: missing patientId');
  const input = { mode: 'revoke', patientId };
  if (granteeRef) input.granteeRef = granteeRef;
  return execAccessBot(input);
}
